import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useOutletContext } from 'react-router'
import clsx from 'clsx'
import { FaArrowLeft } from 'react-icons/fa'

import { CurrentUser } from '../../services/schema/User'
import { useUpdateUserProfileMutation } from '../../services/apiSlice'
import Filler from '../utils/Filler'
import MediaContainer from './MediaContainer'
import Input from '../forms/Input'
import DateInput from '../forms/DateInput'
import { errorHandler } from '../../services/helpers'
import { dateInputFormatter } from '../../services/helpers'
import { emitToCustomErrorDisplay } from '../custom/utils/emitError'
import { UserUpdates } from '../../services/schema/User'
import { uploadMedia } from '../../services/api'
import { sleep } from '../../utils/etc/sleep'
import { frontendRoutes } from '../../features/Auth/CreateAccount'
import { containsAnySpecialCharacter } from '../../services/helpers'

const Profile: React.FC = () => {
  const navigate = useNavigate()
  const user = useOutletContext<CurrentUser>()
  const [uploadLoading, setUploadLoading] = useState<{
    active: boolean
    progress: string
    message: string
    success: boolean
  }>({
    active: false,
    progress: 'start',
    message: '',
    success: true,
  })
  const [updateUserProfile, { data, isSuccess, isError, error }] =
    useUpdateUserProfileMutation()

  useEffect(() => {
    if (data && isSuccess) {
      setUploadLoading((curr) => {
        return {
          ...curr,
          progress: 'finish',
          message: 'Update complete!',
        }
      })

      setTimeout(() => {
        setUploadLoading((curr) => {
          return {
            ...curr,
            active: false,
            progress: 'start',
            message: '',
          }
        })
      }, 3000)
    }

    if (isError) {
      // someday I'll refactor this error handling and types mess...
      const err = errorHandler(error)
      const message =
        err && err.errorCode === 403
          ? 'User info change limit reached!'
          : 'Update failed, please try again later.'
      setUploadLoading((curr) => {
        return {
          ...curr,
          progress: 'finish',
          message: message,
          success: false,
        }
      })

      setTimeout(() => {
        setUploadLoading((curr) => {
          return {
            ...curr,
            active: false,
            progress: 'start',
            message: '',
            success: true,
          }
        })
      }, 3000)
    }
  }, [data, isSuccess, isError, error])

  if (user) {
    const defaultProfilePic = user.profilePicture
      ? user.profilePicture
      : '/user_sample.jpg'

    const defaultHeaderPic = user.headerPicture ? user.headerPicture : null

    const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      const formData = new FormData(e.currentTarget)
      const pp = formData.get('profilePicture') as File
      const hp = formData.get('headerPicture') as File
      const dn = formData.get('displayName') as string
      const bt = formData.get('bioText') as string
      const dob = formData.get('date of birth') as string

      const updates: UserUpdates = {
        profilePicture: null,
        headerPicture: null,
        profilePicturePublicId: null,
        headerPicturePublicId: null,
        displayName: null,
        bioText: null,
        dateOfBirth: null,
        email: user.email,
      }
      const mediaUploads: { file: File; type: string }[] = []
      let changesFlag = false

      if (pp.name !== '' || pp.size > 0) {
        mediaUploads.push({ file: pp, type: 'profilePicture' })
        changesFlag = true
      }

      if (hp.name !== '' || hp.size > 0) {
        mediaUploads.push({ file: hp, type: 'headerPicture' })
        changesFlag = true
      }

      if (user.displayName !== dn) {
        if (frontendRoutes.includes(dn)) {
          emitToCustomErrorDisplay('Username already in use...')
          return
        }

        if (containsAnySpecialCharacter(dn)) {
          emitToCustomErrorDisplay(
            'Special characters in display name is not allowed.',
          )
          return
        }
        updates.displayName = dn
        changesFlag = true
      }

      if (user.bioText !== bt) {
        updates.bioText = bt
        changesFlag = true
      }

      if (dateInputFormatter(user.dates.dateOfBirthNum) !== dob) {
        updates.dateOfBirth = dob
        changesFlag = true
      }

      if (!changesFlag) {
        emitToCustomErrorDisplay('No changes in user profile...')
        return
      }

      try {
        if (confirm('Are you sure you want to save changes?')) {
          setUploadLoading((curr) => {
            return {
              ...curr,
              active: true,
              message:
                'Please do not navigate away from this page while update changes/uploads are ongoing...',
            }
          })

          if (mediaUploads.length > 0) {
            const uploadData = await uploadMedia(mediaUploads, 'image')
            uploadData.forEach((d) => {
              if (d.type === 'profilePicture') {
                updates.profilePicture = d.mediaUrl
                updates.profilePicturePublicId = d.mediaPublicId
              } else {
                updates.headerPicture = d.mediaUrl
                updates.headerPicturePublicId = d.mediaPublicId
              }
            })
          }

          await sleep(1000)
          setUploadLoading((curr) => {
            return {
              ...curr,
              progress: 'start-mid',
            }
          })

          setUploadLoading((curr) => {
            return {
              ...curr,
              progress: 'mid',
            }
          })
          setUploadLoading((curr) => {
            return {
              ...curr,
              progress: 'mid-finish',
            }
          })

          updateUserProfile({
            currUsername: user.username,
            updates: updates,
          })
        }
      } catch (err) {}
    }

    let main = (
      <>
        <div className="relative flex w-full grow">
          <div className="h-dvh" />
          <div className="border-theme bgd-theme relative flex h-full w-full max-w-[600px] min-w-0 grow flex-col items-stretch border-r border-b border-l md:mr-0">
            <form
              onSubmit={handleSave}
              autoComplete="off"
              className="h-full w-full"
            >
              <div className="border-theme bgd-theme relative flex h-full w-full max-w-[600px] min-w-0 grow flex-col items-stretch border-r border-b border-l md:mr-0">
                <Filler />
                <div className="border-theme sticky top-0 left-0 z-[100] flex h-[60px] w-full items-center justify-between border-b bg-inherit pl-[10px] text-xl font-bold">
                  <div className="flex h-full items-center">
                    <button
                      type="button"
                      onClick={() => {
                        navigate(`/${user.username}`, {
                          replace: true,
                        })
                      }}
                      className="hover-main flex h-[40px] w-[40px] items-center justify-center rounded-full text-sm"
                    >
                      <FaArrowLeft />
                    </button>
                    <p className="pl-[20px] tracking-wide">Edit Profile</p>
                  </div>
                  <button
                    type="submit"
                    className="border-theme bgd-theme-wb text-main-invert mr-[15px] cursor-pointer rounded-full border px-[15px] py-[5px] text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    // disabled={true}
                  >
                    Save
                  </button>
                </div>
                <div
                  className={clsx('transition-all duration-300', {
                    'h-0': !uploadLoading.active,
                    'h-auto': uploadLoading.active,
                  })}
                >
                  <div className="bgd-gray h-[10px] w-full transition-all duration-300">
                    <div
                      className={clsx(
                        'bgd-blue h-full transition-all duration-300',
                        {
                          'w-[1%]': uploadLoading.progress === 'start',
                          'w-[25%]': uploadLoading.progress === 'start-mid',
                          'w-[50%]': uploadLoading.progress === 'mid',
                          'w-[75%]': uploadLoading.progress === 'mid-finish',
                          'w-[100%]': uploadLoading.progress === 'finish',
                          'bgd-red': !uploadLoading.success,
                        },
                      )}
                    ></div>
                  </div>
                  <div
                    className={clsx(
                      'bgd-gray flex h-auto w-full items-center justify-center px-[15px] py-[25px] text-sm font-semibold',
                      {
                        'text-red': !uploadLoading.success,
                      },
                    )}
                  >
                    <h1>{uploadLoading.message}</h1>
                  </div>
                </div>
                <div className="relative flex flex-col">
                  <div className="h-[200px] w-full">
                    <MediaContainer
                      inputName="headerPicture"
                      defaultPic={defaultHeaderPic}
                    />
                  </div>
                  <div className="relative h-auto cursor-pointer">
                    <div className="bgd-theme absolute top-[-65px] left-[25px] h-[130px] w-[130px] rounded-full">
                      <MediaContainer
                        inputName="profilePicture"
                        defaultPic={defaultProfilePic}
                      />
                    </div>
                    <div className="flex flex-col px-[25px] pt-[100px]">
                      <Input
                        display={'Name'}
                        type={'text'}
                        name={'displayName'}
                        value={user.displayName}
                        min={8}
                        max={50}
                        showLimit={true}
                        // inputErrorFlag={inputErrorFlags.includes(0)}
                      />
                      <Input
                        display={'Bio'}
                        type={'text'}
                        name={'bioText'}
                        value={user.bioText}
                        min={0}
                        max={100}
                        showLimit={true}
                        // inputErrorFlag={inputErrorFlags.includes(0)}
                      />
                      <div
                        className={clsx('w-full text-base font-bold', {
                          // 'text-red': inputErrorFlags.includes(4),
                        })}
                      >
                        <h1 className="pb-[5px]">Date of Birth:</h1>
                      </div>
                      <div className="w-full text-base font-bold">
                        <DateInput value={user.dates.dateOfBirthNum} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </>
    )
    return <>{main}</>
  } else {
    // no user detected
    // we reroute to landing? useEffect on top then
  }
}

export default Profile
